import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { 
  FiShoppingBag, 
  FiSearch, 
  FiFilter,
  FiChevronDown,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiPlus,
  FiCopy,
  FiDollarSign,
  FiUsers,
  FiClock,
  FiTrendingUp,
  FiAlertCircle,
  FiCheckCircle,
  FiXCircle,
  FiDownload,
  FiArchive,
  FiRefreshCw,
  FiX,
  FiTrash,
  FiPlus as FiPlusIcon
} from 'react-icons/fi';
import { 
  FaFacebook, 
  FaInstagram, 
  FaTwitter, 
  FaWhatsapp, 
  FaTelegram, 
  FaTiktok,
  FaLinkedin,
  FaYoutube,
  FaReddit,
  FaDiscord,
  FaSnapchat,
  FaPinterest,
  FaTumblr,
  FaSlack,
  FaTwitch,
  FaGithub,
  FaEnvelope,
  FaMobileAlt,
  FaShieldAlt
} from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
import { 
  fetchAdminAccounts,
  createAccount,
  updateAccount,
  deleteAccount
} from '../../store/slices/accountSlice';
import { GlassCard } from '../../components/ui/GlassCard';
import { LoadingSkeleton } from '../../components/ui/LoadingSkeleton';
import { format, isValid } from 'date-fns';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export const AdminAccountsPage = () => {
  const dispatch = useDispatch();
  const { accounts, isLoading } = useSelector((state) => state.accounts);
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [customPlatformInput, setCustomPlatformInput] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    socialMediaType: 'Facebook',
    description: '',
    price: '',
    quantity: 1,
    details: [{ data: {} }]
  });

  const [detailFields, setDetailFields] = useState({
    0: []
  });

  useEffect(() => {
    dispatch(fetchAdminAccounts());
  }, [dispatch]);

  useEffect(() => {
    if (accounts) {
      let filtered = [...accounts];

      if (searchTerm) {
        filtered = filtered.filter(a => 
          a.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          a.socialMediaType?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      if (platformFilter !== 'all') {
        filtered = filtered.filter(a => a.socialMediaType === platformFilter);
      }

      filtered.sort((a, b) => {
        switch (sortBy) {
          case 'newest':
            return new Date(b.createdAt) - new Date(a.createdAt);
          case 'oldest':
            return new Date(a.createdAt) - new Date(b.createdAt);
          case 'price_high':
            return (b.price || 0) - (a.price || 0);
          case 'price_low':
            return (a.price || 0) - (b.price || 0);
          case 'quantity_high':
            return (b.quantity || 0) - (a.quantity || 0);
          case 'quantity_low':
            return (a.quantity || 0) - (b.quantity || 0);
          default:
            return 0;
        }
      });

      setFilteredAccounts(filtered);
    }
  }, [accounts, searchTerm, platformFilter, sortBy]);

  const platformIcons = {
    Facebook: FaFacebook, Instagram: FaInstagram, Twitter: FaTwitter,
    'X (Twitter)': FaTwitter, WhatsApp: FaWhatsapp, Telegram: FaTelegram,
    TikTok: FaTiktok, LinkedIn: FaLinkedin, YouTube: FaYoutube,
    Reddit: FaReddit, Discord: FaDiscord, Snapchat: FaSnapchat,
    Pinterest: FaPinterest, Tumblr: FaTumblr, Slack: FaSlack,
    Twitch: FaTwitch, GitHub: FaGithub,
    'Google Voice': FaMobileAlt, 'TextPlus': FaMobileAlt, 'Talkatone': FaMobileAlt,
    'VPN': FaShieldAlt, 'ExpressVPN': FaShieldAlt, 'NordVPN': FaShieldAlt,
    'Surfshark': FaShieldAlt, 'CyberGhost': FaShieldAlt, 'ProtonVPN': FaShieldAlt,
    'WeChat': FaEnvelope, 'Viber': FaMobileAlt, 'Line': FaEnvelope,
    'Messenger': FaEnvelope, 'Skype': FaEnvelope, 'Email': FaEnvelope,
  };

  const platformColors = {
    Facebook: 'from-blue-600 to-blue-400', Instagram: 'from-purple-600 to-pink-500',
    Twitter: 'from-sky-400 to-blue-500', 'X (Twitter)': 'from-gray-800 to-gray-600',
    WhatsApp: 'from-green-500 to-emerald-400', Telegram: 'from-blue-400 to-cyan-400',
    TikTok: 'from-gray-900 to-gray-700', LinkedIn: 'from-blue-700 to-blue-500',
    YouTube: 'from-red-600 to-red-400', Reddit: 'from-orange-600 to-orange-400',
    Discord: 'from-indigo-600 to-purple-500', Snapchat: 'from-yellow-400 to-yellow-300',
    Pinterest: 'from-red-700 to-red-500', Tumblr: 'from-indigo-700 to-indigo-500',
    Slack: 'from-pink-500 to-purple-500', Twitch: 'from-purple-700 to-purple-500',
    GitHub: 'from-gray-800 to-gray-600', 'Google Voice': 'from-blue-600 to-orange-400',
    'TextPlus': 'from-purple-600 to-pink-400', 'Talkatone': 'from-orange-600 to-yellow-400',
    'VPN': 'from-emerald-600 to-teal-500', 'ExpressVPN': 'from-yellow-400 to-orange-500',
    'NordVPN': 'from-orange-600 to-red-500', 'Surfshark': 'from-green-600 to-emerald-500',
    'CyberGhost': 'from-purple-600 to-pink-500', 'ProtonVPN': 'from-purple-700 to-blue-600',
    'WeChat': 'from-green-700 to-green-500', 'Viber': 'from-purple-700 to-purple-500',
    'Line': 'from-green-400 to-green-600', 'Messenger': 'from-blue-600 to-indigo-500',
    'Skype': 'from-sky-500 to-blue-500', 'Email': 'from-gray-600 to-gray-800',
  };

  const platforms = [
    { name: 'Facebook', category: 'Social Media' },
    { name: 'Instagram', category: 'Social Media' },
    { name: 'Twitter', category: 'Social Media' },
    { name: 'X (Twitter)', category: 'Social Media' },
    { name: 'TikTok', category: 'Social Media' },
    { name: 'YouTube', category: 'Social Media' },
    { name: 'LinkedIn', category: 'Social Media' },
    { name: 'Snapchat', category: 'Social Media' },
    { name: 'Reddit', category: 'Social Media' },
    { name: 'Pinterest', category: 'Social Media' },
    { name: 'Tumblr', category: 'Social Media' },
    { name: 'Discord', category: 'Social Media' },
    { name: 'Twitch', category: 'Social Media' },
    { name: 'WeChat', category: 'Social Media' },
    { name: 'Viber', category: 'Social Media' },
    { name: 'Line', category: 'Social Media' },
    { name: 'WhatsApp', category: 'Messaging' },
    { name: 'Telegram', category: 'Messaging' },
    { name: 'Messenger', category: 'Messaging' },
    { name: 'Skype', category: 'Messaging' },
    { name: 'Google Voice', category: 'Voice Services' },
    { name: 'TextPlus', category: 'Voice Services' },
    { name: 'Talkatone', category: 'Voice Services' },
    { name: 'GitHub', category: 'Developer' },
    { name: 'ExpressVPN', category: 'VPN' },
    { name: 'NordVPN', category: 'VPN' },
    { name: 'Surfshark', category: 'VPN' },
    { name: 'CyberGhost', category: 'VPN' },
    { name: 'ProtonVPN', category: 'VPN' },
    { name: 'Slack', category: 'Workspace' },
    { name: 'Email', category: 'Email' },
  ];

  const handleQuantityChange = (newQuantity) => {
    const qty = Math.max(1, newQuantity);
    setFormData(prev => {
      // Create details array based on quantity with proper deep copies
      const newDetails = Array(qty).fill(null).map((_, i) => ({
        data: { ...(prev.details[i]?.data || {}) }
      }));
      return { ...prev, quantity: qty, details: newDetails };
    });
  };

  const handleDetailFieldChange = (detailIndex, fieldName, value) => {
    setFormData(prev => {
      const updatedDetails = prev.details.map((detail, idx) =>
        idx === detailIndex
          ? { data: { ...detail.data, [fieldName]: value } }
          : { data: { ...detail.data } }
      );
      return { ...prev, details: updatedDetails };
    });
  };

  const addDetailField = (detailIndex) => {
    const existingFields = detailFields[detailIndex] || Object.keys(formData.details[detailIndex]?.data || {});
    const fieldNumber = existingFields.length + 1;
    const newFieldName = `Field ${fieldNumber}`;
    
    setDetailFields(prev => ({
      ...prev,
      [detailIndex]: [...(prev[detailIndex] || Object.keys(formData.details[detailIndex]?.data || {})), newFieldName]
    }));
    
    // Initialize the field in formData with empty value - create proper deep copy
    setFormData(prev => {
      const updatedDetails = prev.details.map((detail, idx) =>
        idx === detailIndex
          ? { data: { ...detail.data, [newFieldName]: '' } }
          : { data: { ...detail.data } }
      );
      return { ...prev, details: updatedDetails };
    });
  };

  const removeDetailField = (detailIndex, fieldIndex) => {
    setDetailFields(prev => ({
      ...prev,
      [detailIndex]: (prev[detailIndex] || []).filter((_, i) => i !== fieldIndex)
    }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleAddAccount = async () => {
    if (!formData.title || !formData.price || formData.quantity < 1) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSaving(true);
    try {
      await dispatch(createAccount(formData)).unwrap();
      toast.success('Account created successfully');
      setShowAddModal(false);
      resetForm();
      dispatch(fetchAdminAccounts());
    } catch (error) {
      toast.error(error.message || 'Failed to create account');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditAccount = async () => {
    if (!formData.title || !formData.price || formData.quantity < 1) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSaving(true);
    try {
      await dispatch(updateAccount({
        id: selectedAccount._id,
        data: formData
      })).unwrap();
      toast.success('Account updated successfully');
      setShowEditModal(false);
      resetForm();
      dispatch(fetchAdminAccounts());
    } catch (error) {
      toast.error(error.message || 'Failed to update account');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsSaving(true);
    try {
      await dispatch(deleteAccount(selectedAccount._id)).unwrap();
      toast.success('Account deleted successfully');
      setShowDeleteModal(false);
      dispatch(fetchAdminAccounts());
    } catch (error) {
      toast.error(error.message || 'Failed to delete account');
    } finally {
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      socialMediaType: 'Facebook',
      description: '',
      price: '',
      quantity: 1,
      details: [{ data: { 'Field 1': '' } }]
    });
    setDetailFields({ 0: ['Field 1'] });
    setCustomPlatformInput('');
    setSelectedAccount(null);
  };

  const openEditModal = (account) => {
    setSelectedAccount(account);
    // Create deep copies of details to avoid frozen objects from server
    const detailsCopy = (account.details || [{ data: {} }]).map(detail => ({
      data: { ...detail.data }
    }));
    
    setFormData({
      title: account.title || '',
      socialMediaType: account.socialMediaType || 'Facebook',
      description: account.description || '',
      price: account.price || '',
      quantity: account.quantity || 1,
      details: detailsCopy
    });
    
    // Initialize detail fields state
    const newDetailFields = {};
    (account.details || []).forEach((detail, i) => {
      newDetailFields[i] = Object.keys(detail.data || {});
    });
    setDetailFields(newDetailFields);
    
    setShowEditModal(true);
  };

  const exportToExcel = () => {
    const data = filteredAccounts.map(a => ({
      Title: a.title,
      Platform: a.socialMediaType,
      Price: `₦${a.price?.toLocaleString()}`,
      Quantity: a.quantity || 0,
      'Available': Math.max(0, (a.quantity || 0) - (a.details?.filter(d => d.used).length || 0)),
      Description: a.description || '',
      'Created Date': (a.createdAt && isValid(new Date(a.createdAt))) ? format(new Date(a.createdAt), 'MMM dd, yyyy') : '',
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Accounts');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, `accounts_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
  };

  const getQuantityRemaining = (account) => {
    const usedCount = account.details?.filter(d => d.used).length || 0;
    return Math.max(0, (account.quantity || 0) - usedCount);
  };

  const totalAccounts = accounts?.length || 0;
  const totalQuantity = accounts?.reduce((sum, a) => sum + (a.quantity || 0), 0) || 0;
  const totalSold = accounts?.reduce((sum, a) => sum + (a.details?.filter(d => d.used).length || 0), 0) || 0;
  const totalValue = accounts?.reduce((sum, a) => sum + (a.price || 0) * (a.quantity || 0), 0) || 0;

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  const staggerContainer = {
    animate: {
      transition: { staggerChildren: 0.1 }
    }
  };

  return (
    <>
      <Helmet>
        <title>Manage Accounts | Admin | Nitro Logs</title>
      </Helmet>

      <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 bg-linear-to-br from-gray-50 via-white to-gray-100">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8"
          >
            <div>
              <h1 className="text-3xl font-display font-bold text-gray-900 mb-2">
                Manage <span className="text-transparent bg-clip-text bg-linear-to-r from-blue-600 to-purple-600">Accounts</span>
              </h1>
              <p className="text-gray-600">
                Add, edit, and manage all social media accounts with details
              </p>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="mt-4 sm:mt-0 px-6 py-3 bg-linear-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 flex items-center space-x-2 shadow-lg"
            >
              <FiPlus className="w-5 h-5" />
              <span>Add New Account</span>
            </button>
          </motion.div>

          {/* Stats Cards */}
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
          >
            <motion.div variants={fadeInUp}>
              <GlassCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <FiShoppingBag className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">{totalAccounts}</h3>
                <p className="text-sm text-gray-500">Total Products</p>
              </GlassCard>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <GlassCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <FiCheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">{totalQuantity}</h3>
                <p className="text-sm text-gray-500">Total Quantity</p>
              </GlassCard>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <GlassCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                    <FiXCircle className="w-6 h-6 text-red-600" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">{totalSold}</h3>
                <p className="text-sm text-gray-500">Sold Items</p>
              </GlassCard>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <GlassCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <FiDollarSign className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">
                  ₦{(totalValue / 1000000).toFixed(1)}M
                </h3>
                <p className="text-sm text-gray-500">Total Value</p>
              </GlassCard>
            </motion.div>
          </motion.div>

          {/* Filters & Search */}
          <motion.div
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="mb-6 bg-white/50 backdrop-blur-xl rounded-2xl p-4 border border-white/20 shadow-lg"
          >
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search accounts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                />
              </div>
              <select
                value={platformFilter}
                onChange={(e) => setPlatformFilter(e.target.value)}
                className="px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-white"
              >
                <option value="all">All Platforms</option>
                {platforms.map(p => (
                  <option key={p.name} value={p.name}>{p.name}</option>
                ))}
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-white"
              >
                <option value="newest">Newest</option>
                <option value="oldest">Oldest</option>
                <option value="price_high">Price: High</option>
                <option value="price_low">Price: Low</option>
                <option value="quantity_high">Quantity: High</option>
                <option value="quantity_low">Quantity: Low</option>
              </select>
              <button
                onClick={exportToExcel}
                className="px-4 py-3 bg-linear-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all flex items-center space-x-2"
              >
                <FiDownload className="w-5 h-5" />
                <span>Export</span>
              </button>
            </div>
          </motion.div>

          {/* Accounts Grid */}
          {isLoading ? (
            <div className="grid grid-cols-1 gap-6">
              {[...Array(6)].map((_, i) => (
                <LoadingSkeleton key={i} className="h-64" />
              ))}
            </div>
          ) : filteredAccounts.length === 0 ? (
            <motion.div
              variants={fadeInUp}
              initial="initial"
              animate="animate"
              className="text-center py-12"
            >
              <FiAlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-600 mb-2">No accounts found</h3>
              <p className="text-gray-500">Try adjusting your filters or add a new account</p>
            </motion.div>
          ) : (
            <motion.div
              variants={staggerContainer}
              initial="initial"
              animate="animate"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {filteredAccounts.map((account) => {
                const PlatformIcon = platformIcons[account.socialMediaType] || FiShoppingBag;
                const gradientClass = platformColors[account.socialMediaType] || 'from-gray-600 to-gray-400';
                const remaining = getQuantityRemaining(account);

                return (
                  <motion.div key={account._id} variants={fadeInUp}>
                    <GlassCard className="p-6 h-full flex flex-col hover:shadow-xl transition-all duration-300">
                      <div className={`w-12 h-12 bg-linear-to-r ${gradientClass} rounded-xl flex items-center justify-center mb-4 text-white`}>
                      {account.title.includes('HMA') ? 
                        <img src="https://i.pinimg.com/736x/0f/3d/23/0f3d234572358e98fb27fd74c20ab3ac.jpg" className="w-full h-full object-cover rounded-xl" alt="" /> 
                        : 
                        <PlatformIcon className="w-6 h-6" />
                      }
                      </div>
                      
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{account.title}</h3>
                      <p className="text-sm text-gray-600 mb-4">{account.description}</p>
                      
                      <div className="space-y-2 mb-4 grow">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Platform:</span>
                          <span className="text-gray-900 font-medium">{account.socialMediaType}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Price:</span>
                          <span className="text-gray-900 font-medium">₦{account.price?.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Total Quantity:</span>
                          <span className="text-gray-900 font-medium">{account.quantity || 0}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Available:</span>
                          <span className={`font-medium ${remaining > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {remaining}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Sold:</span>
                          <span className="text-gray-900 font-medium">{account.details?.filter(d => d.used).length || 0}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Created:</span>
                          <span className="text-gray-900 font-medium">
                            {account.createdAt && isValid(new Date(account.createdAt))
                              ? format(new Date(account.createdAt), 'MMM dd, yyyy')
                              : 'N/A'}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-gray-200 gap-2">
                        <button
                          onClick={() => {
                            setSelectedAccount(account);
                            setShowViewModal(true);
                          }}
                          className="p-2 bg-white/50 rounded-lg hover:bg-blue-50 transition-colors flex-1"
                          title="View Details"
                        >
                          <FiEye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openEditModal(account)}
                          className="p-2 bg-white/50 rounded-lg hover:bg-blue-50 transition-colors flex-1"
                          title="Edit Account"
                        >
                          <FiEdit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedAccount(account);
                            setShowDeleteModal(true);
                          }}
                          className="p-2 bg-white/50 rounded-lg hover:bg-red-50 transition-colors flex-1"
                          title="Delete Account"
                        >
                          <FiTrash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </GlassCard>
                  </motion.div>
                );
              })}
            </motion.div>
          )}

          {/* Add/Edit Modal */}
          <AnimatePresence>
            {(showAddModal || showEditModal) && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                onClick={() => {
                  if (showAddModal) setShowAddModal(false);
                  if (showEditModal) setShowEditModal(false);
                  resetForm();
                }}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="bg-white/90 backdrop-blur-xl max-w-4xl w-full rounded-2xl p-6 max-h-[90vh] overflow-y-auto shadow-2xl border border-white/20"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-semibold text-gray-900">
                      {showAddModal ? 'Add New Account' : 'Edit Account'}
                    </h2>
                    <button
                      onClick={() => {
                        if (showAddModal) setShowAddModal(false);
                        if (showEditModal) setShowEditModal(false);
                        resetForm();
                      }}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <FiX className="w-6 h-6" />
                    </button>
                  </div>

                  <div className="space-y-6">
                    {/* Basic Information */}
                    <div className="bg-linear-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                      
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Title *
                          </label>
                          <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            className="w-full p-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-white"
                            placeholder="e.g., Premium Facebook Business"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Platform *
                          </label>
                          <select
                            name="socialMediaType"
                            value={formData.socialMediaType}
                            onChange={(e) => {
                              setFormData(prev => ({ ...prev, socialMediaType: e.target.value }));
                              if (e.target.value === '') {
                                setCustomPlatformInput('');
                              }
                            }}
                            className="w-full p-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-white"
                            required
                          >
                            <option value="">Select a Platform</option>
                            {platforms.map(p => (
                              <option key={p.name} value={p.name}>{p.name}</option>
                            ))}
                            <option value="Other">➕ Add Custom Platform</option>
                          </select>
                        </div>
                      </div>

                      {formData.socialMediaType === 'Other' && (
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Custom Platform Name
                          </label>
                          <input
                            type="text"
                            value={customPlatformInput}
                            onChange={(e) => setCustomPlatformInput(e.target.value)}
                            onBlur={() => {
                              if (customPlatformInput.trim()) {
                                setFormData(prev => ({ 
                                  ...prev, 
                                  socialMediaType: customPlatformInput.trim() 
                                }));
                                setCustomPlatformInput('');
                              }
                            }}
                            className="w-full p-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-white"
                            placeholder="Enter platform name"
                            autoFocus
                            required
                          />
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Description
                        </label>
                        <textarea
                          name="description"
                          value={formData.description}
                          onChange={handleInputChange}
                          className="w-full p-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-white resize-none"
                          placeholder="Enter account description"
                          rows="3"
                        />
                      </div>
                    </div>

                    {/* Pricing & Quantity */}
                    <div className="bg-linear-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Pricing & Inventory</h3>
                      
                      <div className="w-full grid md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Price (₦) *
                          </label>
                          <input
                            type="number"
                            name="price"
                            value={formData.price}
                            onChange={handleInputChange}
                            className="w-full p-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-white"
                            placeholder="25000"
                            required
                          />
                        </div>

                        <div className="w-full">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Quantity *
                          </label>
                          <div className="w-full flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleQuantityChange(formData.quantity - 1)}
                              className="p-2 bg-red-100 rounded-lg hover:bg-red-200 transition-colors"
                            >
                              <FiTrash className="w-4 h-4 text-red-600" />
                            </button>
                            <input
                              type="number"
                              value={formData.quantity}
                              onChange={(e) => handleQuantityChange(Number(e.target.value))}
                              className="w-full flex-1 p-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-white text-center"
                              min="1"
                              required
                            />
                            <button
                              type="button"
                              onClick={() => handleQuantityChange(formData.quantity + 1)}
                              className="p-2 bg-green-100 rounded-lg hover:bg-green-200 transition-colors"
                            >
                              <FiPlusIcon className="w-4 h-4 text-green-600" />
                            </button>
                          </div>
                        </div>

                        <div className="flex items-end">
                          <div className="w-full">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Total Value
                            </label>
                            <div className="p-3 rounded-xl bg-white border border-gray-200 text-center font-semibold text-gray-900">
                              ₦{(formData.price * formData.quantity || 0).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Account Details Section */}
                    <div className="bg-linear-to-r from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-100">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">
                          Account Details ({formData.quantity} items)
                        </h3>
                        <span className="text-sm text-gray-600">
                          Add details for each item
                        </span>
                      </div>

                      <div className="space-y-4">
                        {formData.details.map((detail, detailIndex) => (
                          <div key={detailIndex} className="bg-white rounded-xl p-4 border border-purple-200">
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="font-semibold text-gray-900">
                                Item #{detailIndex + 1}
                              </h4>
                              {detail.used && (
                                <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-medium">
                                  SOLD
                                </span>
                              )}
                            </div>

                            <div className="space-y-3">
                              {(detailFields[detailIndex] || Object.keys(detail.data || {})).map((fieldName, fieldIndex) => (
                                <div key={fieldIndex} className="flex gap-2 items-end">
                                  <div className="flex-1">
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                      {fieldName}
                                    </label>
                                    <textarea
                                      value={detail.data[fieldName] || ''}
                                      onChange={(e) => handleDetailFieldChange(detailIndex, fieldName, e.target.value)}
                                      className="w-full p-2 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-all text-sm bg-white resize-y"
                                      placeholder={`${fieldName}...`}
                                      disabled={detail.used}
                                      rows={3}
                                    />
                                  </div>
                                  {!detail.used && (
                                    <button
                                      type="button"
                                      onClick={() => removeDetailField(detailIndex, fieldIndex)}
                                      className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                                    >
                                      <FiTrash className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              ))}

                              {!detail.used && (
                                <button
                                  type="button"
                                  onClick={() => addDetailField(detailIndex)}
                                  className="w-full p-2 border border-dashed border-purple-300 rounded-lg text-purple-600 hover:bg-purple-50 transition-colors flex items-center justify-center space-x-2 text-sm font-medium"
                                >
                                  <FiPlusIcon className="w-4 h-4" />
                                  <span>Add Field</span>
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col md:flex-row gap-3 pt-4 border-t border-gray-200">
                      <button
                        onClick={() => {
                          if (showAddModal) setShowAddModal(false);
                          if (showEditModal) setShowEditModal(false);
                          resetForm();
                        }}
                        className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all font-medium"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={showAddModal ? handleAddAccount : handleEditAccount}
                        disabled={isSaving}
                        className="flex-1 px-6 py-3 bg-linear-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
                      >
                        {isSaving ? 'Saving...' : (showAddModal ? 'Create Account' : 'Update Account')}
                      </button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Delete Confirmation Modal */}
          <AnimatePresence>
            {showDeleteModal && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                onClick={() => setShowDeleteModal(false)}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="text-center">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FiAlertCircle className="w-6 h-6 text-red-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Account?</h3>
                    <p className="text-gray-600 mb-6">
                      Are you sure you want to delete "{selectedAccount?.title}"? This action cannot be undone.
                    </p>
                    <div className="flex flex-col md:flex-row gap-3">
                      <button
                        onClick={() => setShowDeleteModal(false)}
                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-medium"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleDeleteAccount}
                        disabled={isSaving}
                        className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
                      >
                        {isSaving ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
};
